import { List, Map } from 'immutable';

import { isCombinationDeleted, isOptionDeleted, isVariantDeleted } from './combinations';
import { getOptionFormatter } from '../taxonomy';
import { isTruthy } from '../bool';
import { getSize } from '../iterable/getSize';

import { CHANNEL_VARIANTS, COMBINATIONS, DELETED, INFLUENCE, VARIANTS } from '../../constants/variations';
import { AMOUNT, WEIGHT_UNITS } from '../../constants/product';
import { CUSTOM_PROPERTY_IDS } from '../../constants/taxonomy';
import { EMPTY, SEPARATOR } from '../../constants';
import { ETSY, SHOPIFY } from '../../constants/channels';
import ATTRIBUTE from '../../constants/attributes';

function shapeEtsyVariationsForAPI({ category, channel, profile, toImmutable, variations }) {
  const payload = {};
  const influence = variations.get(INFLUENCE);

  function reduceVariants(result, variantIndex) {
    const variation = {
      first: variantIndex === 0,
      id: -1 - variantIndex,
      options: toImmutable ? EMPTY.LIST : [],
    };

    const variant = variations.getIn([VARIANTS, variantIndex]);

    if (!variant) {
      if (toImmutable) {
        return result.push(Map(variation));
      }

      result.push(variation);
      return result;
    }

    function reduceOptions(format) {
      return function reduce(options, item) {
        const optionId = item.get('id');
        const value = item.get('value');
        const valueId = item.get('valueId');
        const imageHash = profile ? null : item.get('imageHash', null);
        const label = format(value);
        const option = {
          label,
          value,
          valueId,
          imageHash,
          id: optionId,
          isAvailable: null,
        };

        if (toImmutable) {
          return options.push(Map(option));
        }

        options.push(option);
        return options;
      };
    }

    if (variant.get('id')) {
      variation.id = variant.get('id');
    }

    variation.propertyId = variant.get('property');
    variation.formattedName = variant.get('name');
    variation.influencesPrice = influence.getIn([ATTRIBUTE.PRICE, variantIndex], false);
    variation.influencesQuantity = influence.getIn([ATTRIBUTE.QUANTITY, variantIndex], false);
    variation.influencesSku = influence.getIn([ATTRIBUTE.SKU, variantIndex], false);
    variation.scalingOptionId = variant.get('scale') || null;
    variation.isCustomProperty = variation.propertyId === CUSTOM_PROPERTY_IDS[channel][variantIndex];
    variation.options = variant
      .get('options')
      .reduce(reduceOptions(getOptionFormatter({ channel, scale: variation.scalingOptionId })), variation.options);

    if (toImmutable) {
      return result.push(Map(variation));
    }

    result.push(variation);

    return result;
  }

  function reduceCombinations(result, combination, combinationIndex) {
    function reduceOptions(options, item) {
      const optionId = item.get('optionId');
      const variationId = item.get('variantId');
      const option = { optionId, variationId };

      if (toImmutable) {
        return options.push(Map(option));
      }

      options.push(option);
      return options;
    }

    const offering = {
      id: combination.get('id') || -1 - combinationIndex,
      visibility: combination.get(ATTRIBUTE.VISIBILITY),
      price: !profile || getSize(influence.get(ATTRIBUTE.PRICE)) > 0 ? combination.get(ATTRIBUTE.PRICE) : null,
      quantity: !profile || getSize(influence.get(ATTRIBUTE.QUANTITY)) > 0 ? combination.get(ATTRIBUTE.QUANTITY) : null,
      sku: !profile || getSize(influence.get(ATTRIBUTE.SKU)) > 0 ? combination.get(ATTRIBUTE.SKU) : null,
      variationOptions: combination.get('options').reduce(reduceOptions, toImmutable ? EMPTY.LIST : []),
    };

    if (toImmutable) {
      return result.push(Map(offering));
    }

    result.push(offering);

    return result;
  }

  const value = {
    taxonomyId: category,
    variations: CHANNEL_VARIANTS[channel].reduce(reduceVariants, toImmutable ? EMPTY.LIST : []),
    offerings: variations.get(COMBINATIONS).reduce(reduceCombinations, toImmutable ? EMPTY.LIST : []),
  };

  if (profile && profile.get('profileId')) {
    value.templateid = profile.get('profileId');

    if (profile.get('title')) {
      value.templatename = profile.get('title');
    }
  }

  if (toImmutable) {
    payload.value = Map(value);
  } else {
    payload.value = value;
  }

  return toImmutable ? Map(payload) : payload;
}

function shapeShopifyVariationsForAPI({ channel, profile, toImmutable, variations }) {
  const payload = {};
  const variants = variations.get(VARIANTS);
  const deleted = variations.get(DELETED, EMPTY.MAP);

  function reduceVariants(result, variantIndex) {
    const option = {
      shopify_name: '',
      shopify_position: String(variantIndex + 1),
      shopify_values: toImmutable ? EMPTY.LIST : [],
    };

    const variant = variants.get(variantIndex);

    if (!variant) {
      if (toImmutable) {
        return result.push(Map(option));
      }

      result.push(option);

      return result;
    }

    function reduceOptions(options, item) {
      if (isOptionDeleted({ deleted, option: item, variants, variantIndex })) return options;

      const value = item.get('value');

      if (toImmutable) {
        return options.push(value);
      }

      options.push(value);

      return options;
    }

    option.shopify_name = variant.get('name');
    option.shopify_position = variantIndex + 1;
    option.shopify_values = variant.get('options').reduce(reduceOptions, toImmutable ? EMPTY.LIST : []);

    if (profile && profile.get('profileId')) {
      option.id = variant.get('id') || '';
      option.shopify_template_id = parseInt(profile.get('profileId'));
    }

    if (toImmutable) {
      return result.push(Map(option));
    }

    result.push(option);

    return result;
  }

  function reduceCombinations(result, combination, combinationIndex) {
    if (isCombinationDeleted({ combination, deleted })) return result;

    const option1 = combination.getIn(['options', 0, 'name'], null);
    const option2 = combination.getIn(['options', 1, 'name'], null);
    const option3 = combination.getIn(['options', 2, 'name'], null);
    const physical = combination.get(ATTRIBUTE.PHYSICAL);
    const weight = (physical && combination.get(ATTRIBUTE.WEIGHT)) || (profile ? null : AMOUNT.ZERO);
    const variant = {
      deleted: false,
      id: (profile && !!profile.get('profileId') && combination.get('id')) || '',
      shopify_admin_graphql_api_id: null,
      shopify_created_at: null,
      shopify_updated_at: null,
      shopify_fulfillment_service: null,
      shopify_grams: null,
      shopify_inventory_item_id: null,
      shopify_old_inventory_quantity: null,
      shopify_option1: option1,
      shopify_option2: option2,
      shopify_option3: option3,
      shopify_position: combinationIndex + 1,
      shopify_cost: combination.get(ATTRIBUTE.CPI) || (profile ? null : AMOUNT.ZERO),
      shopify_sku: combination.get(ATTRIBUTE.SKU) || null,
      shopify_price: combination.get(ATTRIBUTE.PRICE) || (profile ? null : AMOUNT.ZERO),
      shopify_weight: weight,
      shopify_image_id: profile ? null : combination.get('imageId', null),
      shopify_barcode: combination.get(ATTRIBUTE.BARCODE) || null,
      shopify_weight_unit: combination.get(ATTRIBUTE.UNIT) || (profile ? null : WEIGHT_UNITS[0]),
      shopify_image_hash: profile ? null : combination.get('imageHash', null),
      shopify_taxable: combination.get(ATTRIBUTE.CHARGE_TAX) || false,
      shopify_compare_at_price: combination.get(ATTRIBUTE.CAP) || (profile ? null : AMOUNT.ZERO),
      shopify_inventory_quantity: combination.get(ATTRIBUTE.QUANTITY) || (profile ? null : AMOUNT.ZERO),
      shopify_requires_shipping: physical || false,
      shopify_harmonized_system_code: combination.get(ATTRIBUTE.HS_CODE) || null,
      shopify_country_code_of_origin: combination.get(ATTRIBUTE.COUNTRY_CODE) || null,
      shopify_inventory_management: combination.get(ATTRIBUTE.TRACK_QUANTITY) ? SHOPIFY : null,
      shopify_inventory_policy: combination.get(ATTRIBUTE.CONTINUE_SELLING) ? 'continue' : 'deny',
    };

    if (profile && profile.get('profileId')) {
      variant.shopify_template_id = profile.get('profileId');
      variant.shopify_title = [option1, option2, option3].filter(isTruthy).join(SEPARATOR.SLASH_SPACE);
    } else {
      variant.shopify_id = 0;
    }

    if (toImmutable) {
      return result.push(Map(variant));
    }

    result.push(variant);

    return result;
  }

  if (isVariantDeleted({ deleted, variants })) {
    const empty = toImmutable ? EMPTY.LIST : [];
    const value = {
      options: toImmutable
        ? List([Map({ id: -1, options: empty }), Map({ id: -2, options: empty }), Map({ id: -3, options: empty })])
        : [{ id: -1, options: empty }, { id: -2, options: empty }, { id: -3, options: empty }],
      variants: empty,
    };

    if (toImmutable) {
      payload.value = Map(value);
    } else {
      payload.value = value;
    }
  } else {
    const value = {
      options: CHANNEL_VARIANTS[channel].reduce(reduceVariants, toImmutable ? EMPTY.LIST : []),
      variants: variations.get(COMBINATIONS).reduce(reduceCombinations, toImmutable ? EMPTY.LIST : []),
    };

    if (toImmutable) {
      payload.value = Map(value);
    } else {
      payload.value = value;
    }
  }

  if (!profile) {
    const shopifyMeta = {
      individualSKU: true,
      individualPrice: true,
      individualBarcode: true,
      individualShipping: true,
    };

    if (toImmutable) {
      payload.shopifyMeta = Map(shopifyMeta);
    } else {
      payload.shopifyMeta = shopifyMeta;
    }
  } else {
    payload.individualSKU = true;
    payload.individualPrice = true;
    payload.individualBarcode = true;
    payload.individualShipping = true;

    if (profile.get('profileId')) {
      const templateId = parseInt(profile.get('profileId'));
      payload.templateId = templateId;

      if (toImmutable) {
        payload.shopifyMeta = Map({ templateId });
      } else {
        payload.shopifyMeta = { templateId };
      }
    }
  }

  return toImmutable ? Map(payload) : payload;
}

export function shapeVariationsForAPI({ category, channel, profile, toImmutable, variations }) {
  switch (channel) {
    case ETSY: {
      return shapeEtsyVariationsForAPI({ category, channel, profile, toImmutable, variations });
    }

    case SHOPIFY: {
      return shapeShopifyVariationsForAPI({ channel, profile, toImmutable, variations });
    }

    default: {
      return undefined;
    }
  }
}
